<script lang="ts">
  /**
   * Reiseübersicht: anlegen, öffnen, löschen (in den Papierkorb).
   *
   * ## Was diese Etappe geändert hat
   *
   * 1. **Kein `auto-fill`-Raster mehr.** Die Karten saßen in
   *    `minmax(220px, 1fr)` und ballten sich links zusammen, während rechts
   *    zwei Drittel leer blieben — zwei Reisen sahen aus wie ein Fehler. Jetzt
   *    eine Spalte auf Lesebreite: Eine Reise ist eine Zeile mit Inhalt, keine
   *    Kachel in einer Galerie.
   * 2. **Die Reiseart steht an der Zeile.** Das ist die einzige Stelle, an der
   *    mehrere Reisen nebeneinander stehen; ohne Kennzeichnung greift man in
   *    die falsche und sucht dann einen Reiter, den es dort nicht gibt.
   * 3. **Kopieren und Löschen sind nicht mehr gleichrangig.** Sie schwebten
   *    als zwei gleich aussehende Symbolknöpfe oben rechts auf der Karte,
   *    deutlich unter 44 px. Jetzt sind es Nebenhandlungen des `Zeilenmuster`s
   *    in 44-px-Zielen, und Löschen ist rot.
   * 4. **Anlegen ist eine eigene Fläche.** Vorher klappte die Auswahl zwischen
   *    Knopf und Liste auf — bei zwei kleinen Schalterpaaren ging sie neben
   *    der Liste unter. Es ist der wichtigste Moment der Anwendung: Hier fällt
   *    die Entscheidung, die über die halbe Oberfläche bestimmt. Sie bekommt
   *    die Fläche allein, ohne dafür ein Fenster aufspringen zu lassen.
   * 5. **Der Papierkorb ist ein Knopf mit Zahl** statt eines `details` in
   *    Kleinschrift. Gut gedacht war er schon, nur nicht zu finden.
   *
   * ## Abgeschlossene Reisen
   *
   * Wer länger plant, sammelt gefahrene Reisen an, und die stehen nach
   * `updated_at` zwischen den kommenden. Sie zu löschen wäre falsch — man
   * schaut sie wieder an —, sie oben zu lassen auch. Also ein Schalter je
   * Zeile und eine eigene Gruppe am Ende. Der Papierkorb ist die andere
   * Frage („weg damit") und bleibt, wo er war.
   *
   * ## Fremde Reisen stehen mitten in der Liste
   *
   * Seit dem Mitplanen enthält `GET /trips` beides — eigene und die, an denen
   * man mitplant —, in derselben Sortierung. Zwei getrennte Blöcke wären eine
   * Unterscheidung, die man beim Suchen einer Reise nicht trifft: Man sucht
   * „die Kajak-Rundreise", nicht „eine von Andreas Reisen". Die Zeile sagt es
   * trotzdem, im `zusatz` („von Andrea"), und sie zeigt nur die Knöpfe, die
   * der Server für diesen Betrachter auch bedient.
   *
   * ## Reisen vergleichen
   *
   * Unter jeder **abgeschlossenen** Reise steht eine dezente Zeile
   * `38 €/Tag · 0,21 €/km`. Sie ist der eigentliche Zweck der Kennzahlen: Eine
   * Reise allein sagt mit 0,21 €/km nichts, drei nebeneinander sagen, welche
   * die teure war. Wie sie geladen wird und warum so vorsichtig, steht bei
   * `vergleicheNachladen`.
   *
   * ## Warum das Reisebuch hier nicht mehr steht
   *
   * Bis zum 7.10.2026 trug jede eigene, abgeschlossene Reise hier einen
   * Knopf „Reisebuch erzeugen“, dazu Öffnen-Knöpfe, „Neu setzen“ und die
   * Auswahlmaske. Florian fand das auf der Startseite komisch, und es war
   * auch die falsche Ebene: Die Übersicht wählt zwischen Reisen, das Buch ist
   * eine Sache **einer** Reise. Es steht jetzt im Planer unter
   * Erinnern › Reisebuch (`ReisebuchPanel.svelte`). Hier bleibt davon ein
   * kleiner Verweis an der Zeile, der die Reise gleich auf diesem Reiter
   * öffnet (`reisebuchOeffnen`) — ohne Abfrage, ohne Zustand.
   */
  import { ApiError, api, type Trip, type User } from './api';
  // Die Sätze für den Anfang und die Antwort des Beispielreise-Knopfs stehen
  // dort und nicht hier: Sie sind Text mit Regeln (409 ist keine Störung) und
  // damit ohne Browser prüfbar.
  import {
    BEISPIEL_KNOPF,
    BEISPIEL_WIRKUNG,
    ERKLAERSATZ,
    beispielmeldung,
    istRuhig
  } from './onboarding';
  import { reisename } from './planner/reisename';
  import { istAbgeschlossen } from './reiseabschluss';
  // Welche Reise wohin rückt, steht dort und nicht hier — ohne Browser
  // prüfbar. Eine vertauschte Bedingung, und die abgeschlossene Reise stünde
  // ganz oben als „läuft gerade"; im Markup fiele das niemandem auf.
  import {
    GRUPPEN_SCHLUESSEL,
    istZugeklappt,
    kannPlanungsstandWaehlen,
    klappUmschalten,
    mitPlanungsstand,
    planungsstandVon,
    reisegruppen,
    zuLesen,
    zuSchreiben,
    type Gruppenid
  } from './reisegruppen';
  import { alsTag } from './reisephase';
  // Die Regel steht dort und nicht hier, damit sie ohne Browser prüfbar ist —
  // sie schaltet Knöpfe, und ein falsch geschalteter Knopf sieht richtig aus.
  import { istFremd, mitwirkende } from './mitplanen';
  import { vergleichskandidaten, vergleichszeile } from './kosten';
  // Welches Bild eine Reise trägt, entscheidet der Server; welche Zahlen davon
  // brauchbar sind, entscheidet diese Datei — ohne Browser prüfbar. Und
  // `mitTitelbild` ist der einzige Weg, die Wahl zu schreiben, ohne den Rest
  // des Einstellungsblocks zu verlieren.
  import {
    brauchtGezeichnetes,
    mitTitelbild,
    titelbildAus,
    titelbildDerReise,
    type Titelbildwahl
  } from './titelbild';
  import Titelbildwahldialog from './Titelbildwahl.svelte';
  // Wer den Verweis aufs Reisebuch sieht, entscheidet dieselbe Regel wie im
  // Panel — eigen und abgeschlossen. Der Verweis öffnet die Reise auf dem
  // Reiter, über die Merkung je Reise (`navigation/phasen.merkungsSchluessel`).
  import { zeigtReisebuch } from './reisebuch';
  import { merkungsSchluessel } from './navigation/phasen';
  // Body und Meldung stehen dort und nicht hier — beide können still falsch
  // sein, und beide müssen ohne Browser prüfbar bleiben.
  import { KOPIER_VORGABE, kopierbody, kopiermeldung, type Kopierwahl } from './kopieren';
  import Kopierwahldialog from './Kopierwahl.svelte';
  import Zeichen from '../gestaltung/Zeichen.svelte';
  import Zeichenknopf from '../gestaltung/Zeichenknopf.svelte';
  import Panelkopf from '../gestaltung/muster/Panelkopf.svelte';
  import Zeilenmuster from '../gestaltung/muster/Zeilenmuster.svelte';
  import Leerzustand from '../gestaltung/muster/Leerzustand.svelte';
  import Ladezustand from '../gestaltung/muster/Ladezustand.svelte';
  import Hinweis from '../gestaltung/muster/Hinweis.svelte';
  import Reiseartmarke from '../gestaltung/muster/Reiseartmarke.svelte';
  import Reiseartwahl from './Reiseartwahl.svelte';
  import {
    ELEKTRO_UNTERKUNFT_HINWEIS,
    ELEKTRO_UNTERKUNFT_TITEL,
    antriebVon,
    uebernachtungVon,
    unterkunftsnaechte,
    type Antrieb,
    type Uebernachtung
  } from './reiseart';
  import { anzahl } from './planner/sprache';
  // Die Profilwahl beim Anlegen: welches vorgewählt ist, was in den Körper
  // gehört und was dazustehen hat, wenn der Server das Profil nicht kennt.
  // Alles drei kann still falsch sein — Begründung im Kopf von
  // `reiseprofile.ts`.
  import {
    ZULETZT_SCHLUESSEL,
    anlegebody,
    anlegemeldung,
    entwuerfeAus as profilentwuerfe,
    profilzeile,
    vorwahl
  } from './reiseprofile';
  import { zuhauseVollstaendig, zuhausezeile } from './grundlagen/konto';

  interface Props {
    /**
     * Der angemeldete Nutzer — für Profilwahl und Zuhause beim Anlegen.
     *
     * Ein Prop und kein eigener Abruf: `App.svelte` hält den Nutzer ohnehin,
     * und ein zweites `GET /auth/me` hier wäre eine zweite Wahrheit, die nach
     * einer Änderung in den Grundlagen von der ersten abweicht.
     */
    nutzer: User;
    onOpen: (trip: Trip) => void;
  }
  let { nutzer, onOpen }: Props = $props();

  let trips = $state<Trip[]>([]);
  let laedt = $state(true);
  let fehler = $state<string | null>(null);
  let legtAn = $state(false);
  let papierkorb = $state<Trip[]>([]);
  let papierkorbOffen = $state(false);

  /* ── Die Beispielreise ───────────────────────────────────────────────
   *
   * Ein Knopf, zwei Orte: im Leerzustand (dort ist er der zweite Weg neben
   * „Neue Reise") und im ⋯-Menü, sobald Reisen dastehen. Im Menü und nicht in
   * der Kopfzeile, weil man ihn genau einmal braucht — ein zweiter Knopf
   * neben „Neue Reise" stünde für immer da und wäre ab dem zweiten Tag im Weg.
   */
  let beispielLaeuft = $state(false);
  /** Was der Server gesagt hat — `ruhig` bei 409 und 404, siehe `onboarding`. */
  let beispielmeldungText = $state<string | null>(null);
  let beispielRuhig = $state(true);
  /** Das ⋯-Menü der Reiseliste. */
  let mehrOffen = $state(false);


  /**
   * Die zwei Fragen vor dem Anlegen — mit **nichts** vorgewählt.
   *
   * Beide `null`, bis geantwortet wird, und erst dann ist der Knopf scharf.
   * Vorher stand die Vorgabe schon da, damit man die Frage nicht bemerken
   * muss; genau das war der Fehler. Wer sie überklickt, sucht später den
   * Reiter Laden in einer Reise, die keinen hat. Zwei Klicks sind der Preis
   * dafür, dass die Entscheidung eine ist — und der Satz unten nimmt ihr das
   * Gewicht, weil beides änderbar bleibt.
   */
  let wahlOffen = $state(false);
  let antrieb = $state<Antrieb | null>(null);
  let uebernachtung = $state<Uebernachtung | null>(null);
  const fertig = $derived(Boolean(antrieb && uebernachtung));

  /* ── Profil und Zuhause ──────────────────────────────────────────────
   *
   * Die zwei Fragen von 2026 bleiben die ersten zwei, und diese beiden kommen
   * **darunter** — als Vorwahl und als Schalter, nicht als weitere Fragen.
   * Der Unterschied ist Absicht: Reiseart und Übernachtung stehen auf `null`,
   * bis geantwortet wird (wer sie überklickt, sucht später einen Reiter, den
   * es nicht gibt). Ein Profil ist dagegen nie falsch — es setzt Werte, die
   * alle in den Reiseeinstellungen stehen und dort änderbar sind. Drei
   * Pflichtfragen vor der ersten Station wären eine Konfiguration.
   */

  /** Die Profile des Kontos, als Entwürfe (für Name und Kurzzeile). */
  const profile = $derived(profilentwuerfe(nutzer.reiseprofile));
  /**
   * Das gewählte Profil — `null` heißt „ohne Profil".
   *
   * „Ohne Profil" bleibt ausdrücklich zu haben: Eine Reise, für die keine der
   * Vorlagen passt, soll mit den eingebauten Vorgaben anfangen können, statt
   * eine falsche Vorgabe erst wieder aufräumen zu müssen.
   */
  let profilId = $state<string | null>(null);
  /** „Zuhause als Start und Ziel" — nur, wenn am Konto eines steht. */
  let vonZuhause = $state(false);
  /** Was nach dem Anlegen dazustand — etwa: Server kennt keine Profile. */
  let anlegehinweis = $state<string | null>(null);

  const hatZuhause = $derived(zuhauseVollstaendig(nutzer.zuhause));
  const zuhausetext = $derived(zuhausezeile(nutzer.zuhause));

  /**
   * Das zuletzt benutzte Profil aus diesem Browser.
   *
   * `localStorage` und nicht das Konto (Begründung an
   * `reiseprofile.ZULETZT_SCHLUESSEL`): Es ist eine Bequemlichkeit dieses
   * Geräts. Der Zugriff steht in `try`, weil `localStorage` im privaten
   * Fenster und bei abgeschalteten Website-Daten wirft — und eine
   * Reiseliste, die deswegen nicht aufgeht, wäre der schlechteste Tausch
   * gegen eine Vorwahl.
   */
  function zuletztGelesen(): string | null {
    try {
      return localStorage.getItem(ZULETZT_SCHLUESSEL);
    } catch {
      return null;
    }
  }

  function zuletztGemerkt(id: string | null) {
    try {
      if (id) localStorage.setItem(ZULETZT_SCHLUESSEL, id);
      else localStorage.removeItem(ZULETZT_SCHLUESSEL);
    } catch {
      /* Ohne Gedächtnis steht beim nächsten Mal das erste Profil da. */
    }
  }

  /**
   * Die Vergleichszeile je Reise — `null` heißt „abgerufen, nichts zu sagen".
   *
   * Der Schlüssel ist die Reise-Kennung, nicht ihr Platz in der Liste: Die
   * Liste sortiert sich beim Abhaken um, und ein Index würde die Zeile dabei
   * an die falsche Reise hängen.
   */
  let vergleiche = $state(new Map<number, string | null>());
  /**
   * Läuft gerade ein Abruf? Absichtlich **kein** `$state`.
   *
   * Es steuert nur den Ablauf und wird nirgends angezeigt; als Zustand
   * gehalten löste jedes Setzen ein Neuzeichnen der ganzen Liste aus.
   */
  let vergleichLaeuft = false;
  /**
   * Für dieses Konto gibt es die Zeile nicht — und zwar für keine Reise.
   *
   * `GET /trips/{id}/kosten` hängt am Schalter „Reisetagebuch" (die Ist-Werte
   * *sind* Tagebucheinträge). Ein Konto ohne diesen Schalter bekäme bei jeder
   * einzelnen Reise dieselbe 403; nach der ersten hört das Nachladen deshalb
   * ganz auf. Sichtbar wird davon nichts — eine Reiseliste, die sich über
   * einen fehlenden Zusatz beschwert, ist schlechter als eine ohne Zusatz.
   */
  let kostenGesperrt = false;

  async function laden() {
    laedt = true;
    fehler = null;
    try {
      trips = await api.trips();
      papierkorb = await api.deletedTrips().catch(() => []);
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Laden fehlgeschlagen.';
    } finally {
      laedt = false;
    }
    // Bewusst **nicht** abgewartet: Die Liste steht, und die Kennzahlen
    // tröpfeln danach hinein. Ein `await` hier hielte die ganze Übersicht auf,
    // bis die letzte alte Reise durchgerechnet ist.
    void vergleicheNachladen();
    void titelbilderNachladen();
  }
  laden();

  /**
   * Die Beispielreise ins Konto kopieren und öffnen.
   *
   * Geöffnet wird sie nur, wenn sie **neu** entstanden ist: Bei einem 409
   * liegt sie schon irgendwo in der Liste, und dorthin zu springen wäre ein
   * Sprung in eine Reise, an der der Nutzer womöglich seit Wochen arbeitet.
   */
  async function beispielAnlegen() {
    beispielLaeuft = true;
    beispielmeldungText = null;
    mehrOffen = false;
    try {
      const reise = await api.beispielreise();
      await laden();
      onOpen(reise);
    } catch (e) {
      const status = e instanceof ApiError ? e.status : null;
      beispielRuhig = istRuhig(status);
      beispielmeldungText = beispielmeldung(
        status,
        e instanceof Error ? e.message : null
      );
      // Bei einem 409 steht die Kopie schon da — die Liste zeigt sie, sobald
      // sie frisch ist. Ohne das Nachladen sucht man eine Reise, die der
      // Hinweis gerade verspricht.
      if (status === 409) await laden();
    } finally {
      beispielLaeuft = false;
    }
  }

  /**
   * Die Kennzahlen der abgeschlossenen Reisen — eine nach der anderen.
   *
   * ## Warum nicht alle auf einmal
   *
   * `Promise.all` über die abgeschlossenen Reisen wäre eine Zeile kürzer und
   * schickt beim Öffnen der Übersicht N Anfragen gleichzeitig los. Jede davon
   * liest auf dem Server Stationen, Etappen, das ganze Tagebuch und den
   * Plan-Schnappschuss und rechnet die Auswertung durch — für eine Zeile mit
   * zwei Zahlen, die niemand angefordert hat. Bei einem Konto mit fünfzehn
   * gefahrenen Reisen ist das der teuerste Griff der ganzen Anwendung, und er
   * fällt ausgerechnet auf den Moment, in dem jemand nur eine Reise öffnen
   * will.
   *
   * Also: höchstens eine Anfrage unterwegs, in der Reihenfolge der Liste. Die
   * Zeilen erscheinen nacheinander von oben nach unten, und wer inzwischen
   * eine Reise öffnet, nimmt dem Server nichts weg.
   *
   * ## Wann abgebrochen wird
   *
   * Beim Wechsel auf die Anlegen-Fläche (`wahlOffen`) — dann ist die Liste
   * nicht mehr zu sehen, und weiterzurechnen wäre Arbeit für niemanden. Und
   * bei einer 403, siehe `kostenGesperrt`.
   *
   * Ein Fehlschlag an einer einzelnen Reise wird als `null` vermerkt und nicht
   * wiederholt: Er ist still (die Reiseliste darf an einem Kostenabruf nicht
   * scheitern), und ein zweiter Versuch brächte dieselbe Antwort.
   */
  async function vergleicheNachladen() {
    if (vergleichLaeuft || kostenGesperrt) return;
    vergleichLaeuft = true;
    try {
      for (const id of vergleichskandidaten(trips, new Set(vergleiche.keys()))) {
        if (wahlOffen) break;
        let zeile: string | null = null;
        try {
          zeile = vergleichszeile((await api.kosten(id)).kennzahlen);
        } catch (e) {
          if (e instanceof ApiError && e.status === 403) {
            kostenGesperrt = true;
            return;
          }
        }
        // Neue `Map` statt `set` am Ort: Svelte sieht die Änderung sonst nicht.
        vergleiche = new Map(vergleiche).set(id, zeile);
      }
    } finally {
      vergleichLaeuft = false;
    }
  }

  /* ── Das Titelbild ──────────────────────────────────────────────────
   *
   * Eine Reise mit Fotos zeigt eines davon über ihrer Zeile. Welches, hat der
   * Server schon entschieden (`titelbild_eintrag`/`titelbild_medium`, Vorrang
   * für `settings.titelbild` — siehe `lib/titelbild.ts`); hier ist nur noch zu
   * laden.
   *
   * Objekt-URL je Reise, Schlüssel ist die Reise-Kennung und nicht ihr Platz
   * in der Liste — derselbe Grund wie bei `vergleiche`: Die
   * Liste sortiert sich beim Abhaken um, und ein Index hängte das Bild an die
   * falsche Reise.
   */
  let titelbilder = $state(new Map<number, string>());
  /**
   * Welche dieser Bilder **gezeichnet** sind statt fotografiert.
   *
   * Sie tragen auf der Kachel ein Etikett, und dafür gibt es nur einen Grund:
   * Ein abstraktes Bild über einer Reise sieht auf den zweiten Blick aus wie
   * ein Foto von unterwegs. Es ist keines, es zeigt keinen Ort dieser Reise,
   * und der Unterschied muss an der Kachel stehen und nicht in der Hilfe.
   */
  let gezeichnete = $state(new Set<number>());
  /**
   * Welche Reisen schon versucht wurden — auch die, bei denen nichts kam.
   *
   * Ohne dieses Gedächtnis liefe die Schleife bei jedem Aufruf wieder über die
   * Reise, deren Foto gelöscht ist, und holte sich denselben 404. Absichtlich
   * **kein** `$state`: Es steuert den Ablauf und wird nirgends angezeigt.
   */
  const titelbildVersucht = new Set<number>();
  /** Läuft gerade ein Abruf? Aus demselben Grund kein `$state` wie oben. */
  let titelbildLaeuft = false;
  /**
   * Jede erzeugte Objekt-URL, zum Freigeben beim Verlassen der Übersicht.
   *
   * Eine schlichte Liste neben der `Map`, und das hat einen Grund: Der
   * Aufräumer darf `titelbilder` nicht **lesen**, sonst liefe der Effekt bei
   * jedem neuen Bild noch einmal an und gäbe dabei die URLs frei, die gerade
   * angezeigt werden. Dieselbe Sorgfalt wie in `Medium.svelte`, nur für viele
   * Bilder statt für eines: Zwanzig Vorschauen zu je einem Viertelmegabyte
   * bleiben sonst bis zum Neuladen im Speicher.
   */
  const objektUrls: string[] = [];
  $effect(() => () => {
    for (const url of objektUrls) URL.revokeObjectURL(url);
  });

  /**
   * Die Titelbilder holen — eines nach dem anderen.
   *
   * Dasselbe Muster und derselbe Grund wie bei `vergleicheNachladen`: Ein
   * Konto mit zwanzig Reisen schickte sonst zwanzig Anfragen gleichzeitig los,
   * jede über den Bildpfad mit Blob-Antwort, und zwar genau in dem Moment, in
   * dem jemand nur eine Reise öffnen will. Nacheinander ist nicht langsamer,
   * aber es nimmt dem Server nichts weg — die Bilder erscheinen von oben nach
   * unten.
   *
   * **Fehler sind still.** Der Server prüft bei der Auskunft nicht nach, ob
   * das Medium noch existiert; ein gelöschtes Foto ergibt hier einen 404, und
   * die Zeile steht dann da wie vor dieser Etappe. Eine Reiseliste, die sich
   * über ein fehlendes Vorschaubild beschwert, wäre schlechter als eine ohne
   * Bild.
   */
  async function titelbilderNachladen() {
    if (titelbildLaeuft) return;
    titelbildLaeuft = true;
    try {
      for (const t of trips) {
        // Auf der Anlegen-Fläche ist die Liste nicht zu sehen; weiterzuladen
        // wäre Arbeit für niemanden.
        if (wahlOffen) break;
        if (titelbildVersucht.has(t.id)) continue;
        const wahl = titelbildDerReise(t);
        if (!wahl) {
          // Kein Foto — dann fragt diese Reise nach einem gezeichneten Bild.
          // Die Vorrangregel dahinter steht in `brauchtGezeichnetes`.
          if (brauchtGezeichnetes(t, titelbilder)) {
            titelbildVersucht.add(t.id);
            await gezeichnetesNachladen(t);
          }
          continue;
        }
        titelbildVersucht.add(t.id);
        try {
          const url = await api.medienHolen(t.id, wahl.eintrag, wahl.medium, true);
          objektUrls.push(url);
          // Neue `Map` statt `set` am Ort: Svelte sieht die Änderung sonst nicht.
          titelbilder = new Map(titelbilder).set(t.id, url);
        } catch {
          // Still — siehe oben.
        }
      }
    } finally {
      titelbildLaeuft = false;
    }
  }

  /**
   * Das gezeichnete Titelbild einer Reise ohne Foto.
   *
   * ## Die Vorrangregel entsteht in dieser Datei
   *
   * **Wahl > gefundenes Foto > gezeichnetes Bild.** Der Server prüft das nicht
   * nach: `titelbild-ki.svg` liefert eine Zeichnung, wann immer man fragt, und
   * fragt nicht zurück, ob die Reise auch ein echtes Bild hätte. Gefragt wird
   * deshalb ausschließlich hier und ausschließlich dann, wenn
   * `brauchtGezeichnetes` es sagt — eine Zeile weiter oben, eine Zeile zu
   * früh, und jede Reise mit Fotos trüge eine Zeichnung.
   *
   * ## Der erste Abruf dauert
   *
   * Zehn bis zwanzig Sekunden, denn der Server zeichnet dann. Das trägt der
   * sequenzielle Lader: Er hat ohnehin nur eine Anfrage unterwegs, und die
   * Kachel bleibt solange in dem stillen Leerzustand, den sie heute schon hat.
   * Ausdrücklich **kein** Kreisel je Zeile — zwanzig davon nebeneinander wären
   * ein Flimmern für etwas, das niemand angefordert hat. Und ausdrücklich kein
   * eigenes Zeitlimit: Es träfe genau den einen legitimen langsamen Fall.
   *
   * ## Zwei Absagen, zwei Bedeutungen, dieselbe Folge
   *
   * **404** heißt: Auf diesem Server ist keine Bilderzeugung eingerichtet. Das
   * ist kein Fehler, sondern eine Eigenschaft der Installation — die Kachel
   * bleibt grau wie bisher, und für diese Sitzung wird nicht wieder gefragt.
   *
   * **503** heißt: gerade nicht, später vielleicht. Auch das wird für diese
   * Sitzung gemerkt, und zwar aus einem anderen Grund: Ein Lader, der es
   * gleich noch einmal versucht, macht aus einem überlasteten Server einen
   * belagerten. Das Neuladen der Seite ist der Wiederholungsversuch.
   *
   * Das Merken selbst passiert beim Aufrufer (`titelbildVersucht`), vor dem
   * Abruf und für beide Fälle gleich.
   */
  async function gezeichnetesNachladen(t: Trip) {
    try {
      const url = await api.titelbildKiHolen(t.id);
      objektUrls.push(url);
      // Neue `Map`/`Set` statt Ändern am Ort: Svelte sieht es sonst nicht.
      titelbilder = new Map(titelbilder).set(t.id, url);
      gezeichnete = new Set(gezeichnete).add(t.id);
    } catch {
      // Still — siehe oben. Beide Absagen sind erwartbare Antworten und keine
      // Störung, über die eine Reiseliste reden müsste.
    }
  }

  /**
   * Die Reise auf dem Reiter Reisebuch öffnen.
   *
   * Der Planer wählt beim Öffnen den gemerkten Reiter der Reise
   * (`phasen.vorgabeReiter`, je Reise gemerkt). Den hier vorher zu setzen
   * ist der ganze Trick — kein zweiter Weg in den Planer, keine Eigenschaft,
   * die nur dieser eine Verweis braucht. Ohne Speicher (privates Fenster)
   * geht die Reise trotzdem auf, nur eben auf ihrem üblichen Reiter.
   */
  function reisebuchOeffnen(t: Trip) {
    try {
      localStorage.setItem(merkungsSchluessel(t.id), 'reisebuch');
    } catch {
      // Kein Speicher, kein Vorwählen. Geöffnet wird trotzdem.
    }
    oeffnen(t);
  }

  function anlegenOeffnen() {
    antrieb = null;
    uebernachtung = null;
    // Die Vorwahl: das zuletzt benutzte Profil, wenn es noch existiert, sonst
    // das erste. Ein gemerktes Profil, das gelöscht wurde, wäre eine Vorwahl,
    // die es nicht gibt — und die Reise entstünde ohne Profil, obwohl eines
    // dastand.
    profilId = vorwahl(profile, zuletztGelesen());
    vonZuhause = false;
    anlegehinweis = null;
    wahlOffen = true;
  }

  /**
   * Zurück zur Liste — und dort weitermachen, wo das Nachladen aufgehört hat.
   *
   * Ohne den zweiten Aufruf blieben die Reisen unter der Stelle, an der die
   * Anlegen-Fläche aufging, für immer ohne Vergleichszeile: Die Schleife bricht
   * beim Wechsel ab, und von selbst läuft sie erst beim nächsten `laden()`
   * wieder an.
   */
  function anlegenAbbrechen() {
    wahlOffen = false;
    void vergleicheNachladen();
    void titelbilderNachladen();
  }

  async function neu() {
    if (!antrieb || !uebernachtung) return;
    legtAn = true;
    try {
      // Reiseart, Profil und Zuhause gehen **beim Anlegen** mit. Sie danach zu
      // patchen wären drei weitere Schreibvorgänge, von denen jeder scheitern
      // kann — und dann stünde eine Reise da, die anders ist als eben
      // ausgewählt. Der Körper entsteht in `reiseprofile.anlegebody`: Dort
      // steht auch, warum `von_zuhause` ohne hinterlegtes Zuhause nicht
      // mitgeht.
      const t = await api.createTrip(
        anlegebody({ antrieb, uebernachtung, profilId, vonZuhause, hatZuhause })
      );
      zuletztGemerkt(profilId);
      // Hat der Server das Profil angewandt? Erkannt wird es am
      // Herkunftsvermerk und nicht an einer Serverversion — das ist die
      // ehrlichere Prüfung, weil sie das Ergebnis fragt. Der Satz wandert in
      // die Liste, denn hier wird gleich in den Planer gesprungen.
      anlegehinweis = anlegemeldung(t, profilId);
      // Direkt in den Planer: Eine leere Reise in der Liste anzustarren hilft
      // niemandem, und der Name lässt sich dort ohnehin eintragen.
      onOpen({ ...t, stops: [] });
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Anlegen fehlgeschlagen.';
    } finally {
      legtAn = false;
      wahlOffen = false;
    }
  }

  async function oeffnen(t: Trip) {
    try {
      // `mitplaner_von` und `mitplaner` stehen **nur** in der Liste: `GET
      // /trips/{id}` liefert die Reise, wie sie in der Tabelle steht, und dort
      // ist der Besitzername kein Feld. Sie hier mitzunehmen ist billiger und
      // ehrlicher als eine zweite Abfrage für ein Wort — der Planer braucht es
      // im Reiter „Teilen", um den Mitplaner beim Namen zu nennen, von dem
      // seine Reise verwaltet wird.
      const voll = await api.trip(t.id);
      onOpen({ ...voll, mitplaner_von: t.mitplaner_von, mitplaner: t.mitplaner });
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Öffnen fehlgeschlagen.';
    }
  }

  /**
   * Die Kopier-Abfrage — welche Reise, welche Antworten, was schiefging.
   *
   * `kopierziel` ist zugleich der Schalter für den Dialog: Eine Reise heißt
   * offen, `null` heißt zu. Zwei Zustände dafür (ein `offen` und ein `welche`)
   * könnten auseinanderlaufen, und dann stünde ein Dialog ohne Reise da.
   */
  let kopierziel = $state<Trip | null>(null);
  let kopierwahl = $state<Kopierwahl>({ ...KOPIER_VORGABE });
  let kopiertGerade = $state(false);
  /**
   * Der Fehlertext des Servers — bewusst **nicht** der `fehler` der Seite.
   *
   * Der ersetzt die ganze Liste durch „Reisen nicht abrufbar", und das wäre
   * hier gleich zweimal gelogen: Die Reisen sind abrufbar, sie stehen ja da,
   * und der 413 will gelesen und beantwortet werden — im Dialog, in dem man
   * die Belege abwählen kann.
   */
  let kopierfehler = $state<string | null>(null);
  /** Was mit den Belegen passiert ist. Steht über der Liste, bis erneut kopiert wird. */
  let kopiermeldungText = $state<string | null>(null);

  function kopierenOeffnen(t: Trip) {
    kopierziel = t;
    // Bei jedem Öffnen zurück auf die Vorgaben: Die letzte Wahl galt einer
    // anderen Reise, und eine stillschweigend übernommene Antwort auf die
    // Belegfrage kostet Speicher, ohne dass jemand sie noch einmal gesehen hat.
    kopierwahl = { ...KOPIER_VORGABE };
    kopierfehler = null;
    kopiermeldungText = null;
  }

  async function kopieren() {
    const t = kopierziel;
    if (!t || kopiertGerade) return;
    kopiertGerade = true;
    kopierfehler = null;
    try {
      const kopie = await api.duplicateTrip(t.id, kopierbody(kopierwahl));
      // Vorne einsortieren statt neu zu laden — die Kopie ist die neueste Reise.
      trips = [{ ...kopie, stop_count: t.stop_count }, ...trips];
      kopiermeldungText = kopiermeldung(kopie);
      kopierziel = null;
    } catch (e) {
      // Der Servertext bleibt stehen, wie er kommt: Beim 413 nennt er beide
      // Mengen, und dann ist nichts angelegt worden — abwählen und noch einmal.
      kopierfehler = e instanceof Error ? e.message : 'Kopieren fehlgeschlagen.';
    } finally {
      kopiertGerade = false;
    }
  }

  /* ── Die Bildwahl ───────────────────────────────────────────────────
   *
   * `bildziel` ist zugleich der Schalter für den Dialog — dieselbe Bauart und
   * derselbe Grund wie bei `kopierziel`: Eine Reise heißt offen, `null` heißt
   * zu, und zwei getrennte Zustände könnten auseinanderlaufen.
   *
   * Warum die Wahl hier steht und nicht nur im Tagebuch: Sie stand einen Tag
   * lang **nur** dort, an jedem einzelnen Foto. Gesucht wurde sie an der Karte,
   * auf der das Bild zu sehen ist (Befund 27.8.2026). Eine Handlung gehört an
   * ihren Gegenstand.
   */
  let bildziel = $state<Trip | null>(null);

  /**
   * Ein Bild wählen — oder die Automatik wiederherstellen.
   *
   * **`settings` wird als Ganzes ersetzt**, deshalb `mitTitelbild` und nicht
   * `{ titelbild }`: Wer hier nur den einen Schlüssel schickte, verlöre
   * Straßenvorgaben, Auto-Nächte und die abgehakte Maut, und zwar lautlos.
   * Wortgleich zu `Planner.titelbildSetzen` — dieselbe Falle, dieselbe Antwort.
   *
   * Danach wird die Zeile neu bebildert, und dafür braucht es zwei Wege:
   *
   * * **Mit Wahl** ist das neue Titelbild genau die Wahl — der Server rechnet
   *   denselben Vorrang, und die beiden Felder lassen sich hier setzen.
   * * **Ohne Wahl** entscheidet die Unterabfrage des Servers („jüngstes Foto
   *   eines geteilten Eintrags"), und die kennt nur er. `PATCH /trips/{id}`
   *   antwortet mit der reinen Reisezeile ohne die beiden Felder, also wird die
   *   Liste einmal neu geholt. Das ist die seltenere Handlung, und eine
   *   nachgebaute Automatik im Browser wäre die zweite Fassung einer Regel, die
   *   `titelbild.ts` aus gutem Grund gerade nicht nachrechnet.
   *
   * Das Gedächtnis der Zeile (`titelbildVersucht`) muss in beiden Fällen
   * vergessen, was es über diese Reise weiß — es lädt jede ausdrücklich nur
   * einmal, und sonst bliebe das alte Bild stehen.
   */
  async function titelbildSetzen(t: Trip, wahl: Titelbildwahl | null) {
    bildziel = null;
    const settings = mitTitelbild(t.settings, wahl);
    try {
      const frisch = await api.patchTrip(t.id, { settings });
      if (wahl) {
        trips = trips.map((x) =>
          x.id === t.id
            ? { ...x, ...frisch, titelbild_eintrag: wahl.eintrag, titelbild_medium: wahl.medium }
            : x
        );
      } else {
        trips = await api.trips();
      }
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Das Titelbild konnte nicht gespeichert werden.';
      return;
    }
    // Die alte Objekt-URL bleibt in `objektUrls` und wird beim Verlassen der
    // Übersicht freigegeben. Sie hier sofort zurückzuziehen wäre die
    // sparsamere Fassung und die falsche: Zwischen dem Freigeben und dem
    // Eintreffen des neuen Blobs stünde die Zeile mit einem toten `src`.
    titelbildVersucht.delete(t.id);
    // Neue `Map` statt `delete` am Ort: Svelte sieht die Änderung sonst nicht.
    const ohne = new Map(titelbilder);
    ohne.delete(t.id);
    titelbilder = ohne;
    // Und dasselbe für das Etikett: Wer eben ein Foto gewählt hat, darf an
    // seiner Kachel nicht weiter „gezeichnet" lesen.
    const ohneEtikett = new Set(gezeichnete);
    ohneEtikett.delete(t.id);
    gezeichnete = ohneEtikett;
    void titelbilderNachladen();
  }

  async function wiederherstellen(t: Trip) {
    try {
      await api.restoreTrip(t.id);
      papierkorb = papierkorb.filter((x) => x.id !== t.id);
      await laden();
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Wiederherstellen fehlgeschlagen.';
    }
  }

  /**
   * Endgültig löschen.
   *
   * Zwei Rückfragen wären Schikane, eine ist nötig: Danach ist die Reise weg,
   * samt Stationen, Etappen und Reisetagebuch. Deshalb steht der Name in der
   * Rückfrage — wer drei Reisen im Papierkorb hat, soll sehen, welche er
   * gerade verliert.
   */
  async function endgueltigLoeschen(t: Trip) {
    const name = reisename(t).text;
    if (!confirm(`„${name}“ endgültig löschen? Das lässt sich nicht rückgängig machen.`)) return;
    try {
      await api.purgeTrip(t.id);
      papierkorb = papierkorb.filter((x) => x.id !== t.id);
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Löschen fehlgeschlagen.';
    }
  }

  /**
   * Abschließen oder wieder öffnen — optimistisch.
   *
   * Die Zeile wandert sofort, der Server erfährt es danach. Das ist hier
   * richtig und nicht bloß bequem: Der Schalter ist die kleinste denkbare
   * Änderung, und eine halbe Sekunde Warten auf eine Antwort, die zu 99 %
   * „ja" lautet, macht aus einem Handgriff eine Ladeanzeige.
   *
   * Geht es schief, steht die alte Liste wieder da — die ganze, nicht die
   * einzelne Zeile: Zwischen Absenden und Fehlschlag kann eine Kopie
   * dazugekommen sein, und ein Rücksetzen je Zeile ließe die Liste in einem
   * Zustand zurück, den es nie gab.
   *
   * **Keine Rückfrage.** Der Schalter ist verlustfrei umkehrbar; ein
   * `confirm` wäre Reibung an der falschen Stelle. Sie gehört vor das
   * Endgültige — deshalb steht sie beim Papierkorb und hier nicht.
   */
  async function abschlussUmlegen(t: Trip) {
    const vorher = trips;
    const abgeschlossen = !istAbgeschlossen(t);
    // Der lokale Zeitstempel ist eine Vermutung und wird gleich durch den
    // echten ersetzt. Er muss nur eines können: die Zeile in die richtige
    // Gruppe schieben.
    trips = trips.map((x) =>
      x.id === t.id ? { ...x, abgeschlossen_at: abgeschlossen ? new Date().toISOString() : null } : x
    );
    try {
      const frisch = await api.patchTrip(t.id, { abgeschlossen });
      trips = trips.map((x) => (x.id === t.id ? { ...x, ...frisch } : x));
      // Frisch abgehakt heißt: Diese Reise hat jetzt eine Vergleichszeile
      // verdient. Beim Wiederöffnen tut der Aufruf nichts — die Reise ist dann
      // kein Kandidat mehr, und was schon abgerufen ist, bleibt gespeichert.
      void vergleicheNachladen();
    } catch (e) {
      trips = vorher;
      fehler = e instanceof Error ? e.message : 'Ändern fehlgeschlagen.';
    }
  }

  async function loeschen(t: Trip) {
    if (!confirm(`„${reisename(t).text}“ in den Papierkorb verschieben?`)) return;
    try {
      await api.deleteTrip(t.id);
      trips = trips.filter((x) => x.id !== t.id);
      papierkorb = [t, ...papierkorb];
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Löschen fehlgeschlagen.';
    }
  }

  function zeitraum(t: Trip): string {
    if (!t.begin_date) return 'ohne Datum';
    const f = (d: string) => new Date(d + 'T00:00').toLocaleDateString('de-DE');
    return t.end_date && t.end_date !== t.begin_date
      ? `${f(t.begin_date)} – ${f(t.end_date)}`
      : f(t.begin_date);
  }

  /** Zeitraum und Stationszahl in einer Zeile — mit richtiger Pluralform. */
  function unterzeile(t: Trip): string {
    return `${zeitraum(t)} · ${anzahl(t.stop_count ?? 0, 'Station', 'Stationen')}`;
  }

  /* ── Die Ordnung der Liste ──────────────────────────────────────────
   *
   * Die Regel dahinter steht in `reisegruppen.ts` — hier bleibt nur die
   * Anzeige.
   */

  /**
   * Der heutige Tag, **einmal** beim Aufbau gelesen.
   *
   * Dieselbe Entscheidung wie im Planer (`Planner.dichte`, `phase`): Die
   * Reisephase soll sich nicht mitten im Blick auf die Liste ändern, und
   * `reisephase()` nimmt den Tag ausdrücklich als Argument, damit die Uhr
   * genau hier gestellt wird und nicht in der Regel. Wer die Anwendung über
   * Mitternacht offen lässt, lädt sie ohnehin neu, bevor er losfährt.
   */
  const heute = alsTag(new Date());

  const ordnung = $derived(reisegruppen(trips, heute));

  /**
   * Welche Gruppen zugeklappt sind — im Gerät gemerkt.
   *
   * Gelesen wird beim Aufbau, geschrieben beim Klicken und nicht über einen
   * Effekt: Ein Effekt schriebe schon beim ersten Zeichnen einen Eintrag für
   * jemanden, der nie eine Gruppe angefasst hat. Dieselbe Bauart wie der
   * Einklappzustand der Stationsliste.
   */
  let zuGruppen = $state<readonly Gruppenid[]>(zuLesen(gemerkt()));

  /**
   * Der Speichertext — oder `null`, wenn es keinen gibt oder nicht drankommt.
   *
   * `try/catch` um den Zugriff selbst und nicht nur um das Auswerten: In
   * einem Fenster mit gesperrtem Gerätespeicher **wirft** schon
   * `localStorage`, und ein Wurf beim Aufbau der Komponente kostete die ganze
   * Reiseliste. Das tolerante Auswerten übernimmt `zuLesen`.
   */
  function gemerkt(): string | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(GRUPPEN_SCHLUESSEL);
    } catch {
      return null;
    }
  }

  function gruppeKlappen(id: Gruppenid) {
    zuGruppen = klappUmschalten(zuGruppen, id);
    // Ein gesperrter Speicher darf den Griff nicht kosten: Er wirkt dann für
    // diese Sitzung und wird eben nicht gemerkt. Eine Reiseliste, die sich
    // über eine Anzeigevorliebe beschwert, wäre schlechter als eine, die sie
    // vergisst.
    try {
      localStorage.setItem(GRUPPEN_SCHLUESSEL, zuSchreiben(zuGruppen));
    } catch {
      // Still — siehe oben.
    }
  }

  /**
   * Fest geplant ↔ in Planung — optimistisch, wie der Abschluss-Haken.
   *
   * Dieselbe Überlegung wie in `abschlussUmlegen`: Die Zeile wandert sofort,
   * der Server erfährt es danach. Es ist die kleinste denkbare Änderung, und
   * eine halbe Sekunde Warten auf eine Antwort, die zu 99 % „ja" lautet,
   * machte aus einem Handgriff eine Ladeanzeige. Geht es schief, steht die
   * alte Liste wieder da — die ganze, nicht die einzelne Zeile.
   *
   * `mitPlanungsstand` und nicht `{ settings: { planungsstand } }`: Der Block
   * wird als Ganzes ersetzt, und wer nur den einen Schlüssel schickte,
   * verlöre Vignetten, Straßenvorgaben und die Titelbildwahl — lautlos.
   */
  async function planungsstandUmlegen(t: Trip) {
    const vorher = trips;
    const stand = planungsstandVon(t) === 'fest' ? 'offen' : 'fest';
    const settings = mitPlanungsstand(t.settings, stand);
    trips = trips.map((x) => (x.id === t.id ? { ...x, settings } : x));
    try {
      const frisch = await api.patchTrip(t.id, { settings });
      trips = trips.map((x) => (x.id === t.id ? { ...x, ...frisch, settings } : x));
    } catch (e) {
      trips = vorher;
      fehler = e instanceof Error ? e.message : 'Ändern fehlgeschlagen.';
    }
  }

  function abgeschlossenAm(t: Trip): string {
    const d = t.abgeschlossen_at;
    // Der Zeitpunkt gehört dazu: „abgeschlossen" allein ist eine Eigenschaft,
    // „abgeschlossen am 14.9." eine Erinnerung.
    return d ? `abgeschlossen am ${new Date(d).toLocaleDateString('de-DE')}` : 'abgeschlossen';
  }

  function geloeschtAm(t: Trip): string | null {
    const d = (t as Trip & { deleted_at?: string | null }).deleted_at;
    if (!d) return null;
    return `gelöscht am ${new Date(d).toLocaleDateString('de-DE')}`;
  }
</script>

{#if wahlOffen}
  <!--
    Reise anlegen — zwei Fragen, keine Konfiguration.

    Sie ersetzt die Liste, statt über ihr aufzuklappen: Ein Formular mit
    sieben Feldern und Vorgabewerten zwingt einen neuen Nutzer, sieben
    Entscheidungen zu treffen, bevor er irgendetwas gesehen hat. Zwei Fragen
    mit je zwei Antworten sind keine Konfiguration, sondern ein Gespräch.
    Datum, Tagesrhythmus und Grenzen kommen später im Planer, wo man sieht,
    was sie bewirken.
  -->
  <section class="anlegen" aria-label="Neue Reise">
    <header>
      <h2>Neue Reise</h2>
      <p class="dim">Zwei Fragen — danach steht die Reise, und der Rest entsteht auf der Karte.</p>
    </header>

    <Reiseartwahl
      fassung="gross"
      {antrieb}
      {uebernachtung}
      onAntrieb={(a) => (antrieb = a)}
      onUebernachtung={(u) => (uebernachtung = u)}
    />

    <!-- Die interessanteste Kombination bekommt einen eigenen Satz: Sie
         erzeugt eine Frage, die es sonst nirgends gibt. Bei einer gemischten
         Reise stellt sie sich genauso — dort gibt es Unterkunftsnächte, und
         eine davon mit Wallbox ersetzt einen Ladestopp am nächsten Tag. -->
    {#if antrieb === 'elektro' && uebernachtung && unterkunftsnaechte(uebernachtung)}
      <Hinweis art="merk" titel={ELEKTRO_UNTERKUNFT_TITEL}>
        {ELEKTRO_UNTERKUNFT_HINWEIS}
      </Hinweis>
    {/if}

    <!--
      Das Reiseprofil — eine Vorwahl und keine dritte Frage.

      Es steht **unter** den zwei Fragen und ist vorbelegt: Die Reiseart
      entscheidet über ganze Flächen (wer sie überklickt, sucht später einen
      Reiter, den es nicht gibt), ein Profil setzt nur Werte, die alle in den
      Reiseeinstellungen stehen. Drei Pflichtfragen vor der ersten Station
      wären eine Konfiguration und kein Gespräch.

      Radios und kein Auswahlfeld: Es sind vier bis sechs Profile, und an jedem
      steht ein Halbsatz, der sagt, was es bedeutet — in einem `select` wäre
      davon nur der Name zu sehen.
    -->
    {#if profile.length}
      <section class="profilwahl" aria-label="Reiseprofil">
        <h3>Womit fängt die Reise an?</h3>
        <p class="dim">
          Ein Profil setzt Tagesrhythmus, Grenzen, Quartier-Vorlieben und das
          Ladefenster. Alles davon steht danach in den Reiseeinstellungen und
          ist dort änderbar.
        </p>
        <ul class="profile" role="radiogroup" aria-label="Reiseprofil">
          {#each profile as pr (pr.id)}
            <li>
              <button
                type="button"
                role="radio"
                aria-checked={profilId === pr.id}
                class:gewaehlt={profilId === pr.id}
                onclick={() => (profilId = pr.id)}
              >
                <span class="wort">{pr.name}</span>
                <span class="zeile">{pr.beschreibung.trim() || profilzeile(pr)}</span>
              </button>
            </li>
          {/each}
          <li>
            <!-- „Ohne Profil" bleibt zu haben: Eine Reise, für die keine
                 Vorlage passt, soll mit den eingebauten Vorgaben anfangen
                 können, statt eine falsche erst wieder aufzuräumen. -->
            <button
              type="button"
              role="radio"
              aria-checked={profilId === null}
              class:gewaehlt={profilId === null}
              onclick={() => (profilId = null)}
            >
              <span class="wort">ohne Profil</span>
              <span class="zeile">die eingebauten Vorgaben — 09:00 bis 22:00, ohne Grenzen</span>
            </button>
          </li>
        </ul>
      </section>
    {/if}

    <!--
      „Zuhause als Start und Ziel" — nur, wenn am Konto eines steht.

      Kein ausgegrauter Schalter und kein Verweis auf die Kontoseite: Wer keine
      Haustür hinterlegt hat, hat sie bewusst nicht hinterlegt, und ein
      Schalter, der dasteht und nichts tut, ist eine Enttäuschung mit
      Ankündigung. Der Weg dorthin steht in den Grundlagen und wird dort
      erklärt.
    -->
    {#if hatZuhause}
      <label class="zuhause">
        <input type="checkbox" bind:checked={vonZuhause} />
        <span>
          <strong>Zuhause als Start und Ziel</strong>
          <span class="dim">{zuhausetext}</span>
        </span>
      </label>
    {/if}

    {#if fehler}
      <Hinweis art="ernst" titel="Anlegen fehlgeschlagen">
        {#snippet ausweg()}
          <button type="button" class="klein" onclick={neu} disabled={!fertig || legtAn}>
            noch einmal versuchen
          </button>
        {/snippet}
        {fehler}
      </Hinweis>
    {/if}

    <footer>
      {#if fertig && antrieb && uebernachtung}
        <p class="vorschau">
          Deine Reise:
          <Reiseartmarke {antrieb} {uebernachtung} />
        </p>
      {/if}
      <div class="los">
        <button type="button" class="primary" onclick={neu} disabled={!fertig || legtAn}>
          {legtAn ? 'legt an …' : 'Reise anlegen'}
        </button>
        <button type="button" onclick={anlegenAbbrechen} disabled={legtAn}>
          Abbrechen
        </button>
      </div>
      <!-- Der Satz entdramatisiert die Entscheidung: Wer sich hier vertut, hat
           nichts verloren. Und die Reiseart hängt an der Reise, nicht am
           Konto — im August fährt derselbe Mensch anders als im Mai. -->
      <p class="fein">
        Beides lässt sich später in den Reiseeinstellungen ändern. Die Antwort gilt
        für diese Reise, nicht für dein Konto.
      </p>
    </footer>
  </section>
{:else}
  <Panelkopf art="suche" titel="Meine Reisen" offen={laedt || fehler ? null : trips.length}>
    {#snippet handlung()}
      <!-- Beide Griffe in **einer** Hülle: Der Panelkopf verteilt seine Kinder
           mit `space-between`, und drei Kinder stünden auseinandergezogen da —
           der Hauptknopf mitten in der Zeile. -->
      <span class="kopfaktionen">
        <button type="button" class="primary neu" onclick={anlegenOeffnen}>
          <Zeichen name="plus" groesse={16} /> Neue Reise
        </button>
        <!--
          Das ⋯-Menü — für den einen Knopf, den man genau einmal braucht.

          Ein `details`/`summary` und kein eigenes Overlay: Es ist eine Liste
          mit einem Eintrag, und Tastatur, Fokus und Zuklappen kann der Browser
          von Haus aus besser als eine nachgebaute Fassung. Es steht nur da,
          wenn Reisen da sind — im Leerzustand ist der Knopf sichtbar, und ein
          Menü darüber wäre derselbe Weg zweimal.
        -->
        {#if trips.length}
          <details class="mehr" bind:open={mehrOffen}>
            <summary aria-label="Weitere Aktionen">⋯</summary>
            <div class="mehrliste">
              <button type="button" disabled={beispielLaeuft} onclick={beispielAnlegen}>
                {beispielLaeuft ? 'legt an …' : BEISPIEL_KNOPF}
              </button>
              <span class="fein">{BEISPIEL_WIRKUNG}</span>
            </div>
          </details>
        {/if}
      </span>
    {/snippet}
  </Panelkopf>

  <!--
    Der Satz, der sagt, was eine Reise hier überhaupt ist.

    Nur in der einfachen Ansicht (`nur-einfach`, geschaltet über
    `[data-dichte]` — kein Prop, keine Verzweigung hier). Er steht über der
    Liste und nicht im Leerzustand: Gebraucht wird er auch noch bei der
    zweiten Reise, und der Leerzustand darunter sagt etwas anderes, nämlich
    was zu tun ist.
  -->
  <p class="erklaersatz nur-einfach">{ERKLAERSATZ.reiseliste}</p>

  <!-- Was der Beispielreise-Knopf geantwortet hat. `merk` bei 409 und 404:
       Beides ist eine Auskunft und keine Störung — die Kopie liegt schon, oder
       der Betreiber hat keine hinterlegt. -->
  {#if beispielmeldungText}
    <Hinweis art={beispielRuhig ? 'merk' : 'ernst'}>
      {#snippet ausweg()}
        <button type="button" class="klein" onclick={() => (beispielmeldungText = null)}>
          verstanden
        </button>
      {/snippet}
      {beispielmeldungText}
    </Hinweis>
  {/if}

  <!-- Was mit den Belegen passiert ist. Nur nach einer Kopie mit Belegen, und
       nur so lange, bis die nächste beginnt: Eine Meldung, die nach dem Lesen
       stehen bleibt, wird beim nächsten Blick für frisch gehalten. -->
  {#if kopiermeldungText}
    <Hinweis art="merk">{kopiermeldungText}</Hinweis>
  {/if}

  <!-- Der Server hat die Reise angelegt, aber das Profil nicht angewandt.
       Kein Fehler (die Reise steht), aber eine Auskunft, die man braucht: Wer
       sie nicht liest, sucht später die 09:00 aus seinem Profil und findet
       08:00. -->
  {#if anlegehinweis}
    <Hinweis art="merk" titel="Profil nicht angewandt">{anlegehinweis}</Hinweis>
  {/if}

  {#if laedt}
    <Ladezustand zeilen={3} text="holt deine Reisen …" />
  {:else if fehler}
    <!-- Leer wegen eines Fehlers ist **kein** Leerzustand: „Noch keine Reise"
         über drei Reisen auf dem Server wäre die schwerste Art von Falschheit,
         die eine Oberfläche hervorbringen kann. -->
    <Hinweis art="ernst" titel="Reisen nicht abrufbar">
      {#snippet ausweg()}
        <button type="button" class="klein" onclick={laden}>noch einmal versuchen</button>
      {/snippet}
      {fehler} Deine Reisen sind nicht verloren — sie liegen dort und sind gerade nur
      nicht zu erreichen.
    </Hinweis>
  {:else if trips.length === 0}
    <Leerzustand
      art="anfang"
      bild="reise-leer"
      titel="Noch keine Reise"
      text="Leg die erste an — du wirst nach zwei Dingen gefragt, dann setzt du die Stationen per Klick auf die Karte. Oder sieh dir zuerst die Beispielreise an: eine fertige Reise als Kopie in deinem Konto, zum Herumräumen und Wegwerfen."
    >
      {#snippet aktion()}
        <button type="button" class="primary" onclick={anlegenOeffnen}>+ Neue Reise</button>
        <!-- Der zweite Weg, und bewusst der leisere: Wer schon weiß, was er
             vorhat, soll nicht an einer Beispielreise vorbei müssen. Wer es
             nicht weiß, lernt an ihr mehr als an einem leeren Formular. -->
        <button type="button" disabled={beispielLaeuft} onclick={beispielAnlegen}>
          {beispielLaeuft ? 'legt an …' : BEISPIEL_KNOPF}
        </button>
      {/snippet}
    </Leerzustand>
  {:else}
    <!--
      Eine Zeile, zweimal verwendet: oben für die offenen, unten für die
      abgeschlossenen. Zweimal geschrieben liefe sie auseinander — genau das
      ist der Fehler, den `Zeilenmuster` selbst schon einmal vermeidet.
    -->
    {#snippet reisezeile(t: Trip)}
      {@const fertig = istAbgeschlossen(t)}
      {@const fremd = istFremd(t)}
      {@const vergleich = fertig ? (vergleiche.get(t.id) ?? null) : null}
      {@const bild = titelbilder.get(t.id) ?? null}
      <!-- Ein Rahmen um Zeile und Buchleiste: Die Liste ist ein Raster mit
           Abstand, und zwei Geschwister darin stünden mit demselben Abstand
           auseinander wie zwei Reisen. Dann gehörte die Leiste optisch zur
           nächsten Zeile. -->
      <div class="reise" class:mitBild={bild}>
        <!--
          ── Das Titelbild ────────────────────────────────────────────

          Ohne Bild steht hier nichts, und die Zeile sieht aus wie vor dieser
          Etappe — der Normalfall bei einer Reise, die noch nicht gefahren
          ist.

          **Der Name steht nicht auf dem Bild**, obwohl der Entwurf ihn dort
          zeigt. Der Entwurf kennt unter dem Bild nur eine Datumszeile; die
          Zeile hier trägt den Namen ohnehin, und zweimal derselbe Reisename
          untereinander läse sich als Fehler. Wichtiger noch: Der Name ist die
          Beschriftung des Knopfes, der die Reise öffnet — nähme man ihn von
          dort weg, hätte die Zeile für ein Vorlesegerät keinen Namen mehr.

          Und deshalb ist das Bild **kein** zweiter Knopf — für Tastatur und
          Vorlesegerät. Es führte an dieselbe Stelle wie die Zeile darunter,
          und ein zweites Ziel mit demselben Zweck ist dort ein Umweg ohne
          Gewinn. `alt=""` aus demselben Grund — es sagt nichts, was der Name
          nicht sagt.

          Für die Maus aber ist es seit dem 30.9.2026 ein Weg: Florian hat
          beobachtet, dass jeder zuerst aufs Bild klickt. Es ist die größte
          Fläche der Zeile, und dass dort nichts geschah, las sich als kaputt.
          Also bekommt das Bild den Klick und den Zeiger, aber keinen
          `tabindex` und keine Rolle, und `aria-hidden="true"` hält es für ein
          Vorlesegerät beim Schmuck. Der Knopf mit dem Namen bleibt der einzige
          Tastatur- und Vorleseweg. Das Bild tut genau, was er tut —
          `oeffnen(t)`, ohne eigene Sperre, weil der Knopf auch keine hat.
        -->
        {#if bild}
          <!-- Die beiden Warnungen verlangen einen Tastaturweg am Bild. Den gibt
               es mit Absicht nicht: Der Knopf mit dem Namen ist der einzige
               Tastatur- und Vorleseweg, das Bild nur ein zusätzlicher Mausweg,
               weil die Fläche groß ist und jeder zuerst darauf klickt.
               (Svelte 5.56 meldet wegen `aria-hidden` schon nichts; die
               Ausnahmen halten die Begründung dort fest, wo man nach ihr sucht.
               Sie heißen aber auch: Fällt `aria-hidden`, bleibt die Warnung
               stumm — dann ist die Abwägung von Hand neu zu treffen.) -->
          <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <img
            class="cover"
            src={bild}
            alt=""
            aria-hidden="true"
            onclick={() => oeffnen(t)}
          />
          <!--
            Das Etikett am gezeichneten Bild.

            Es steht dort, weil das Bild sonst eine Behauptung wäre: Ein
            abstraktes Titelbild über einer Reise liest sich auf den zweiten
            Blick als Foto von unterwegs, und es ist keines — es zeigt keinen
            Ort dieser Reise, sondern ist zu ihr gezeichnet. Dieselbe Gestalt
            wie die Etiketten der Bildwahl (`Titelbildwahl.svelte`): weiß auf
            einem dunklen Feld, denn ein Bild ist in beiden Themen dasselbe
            Bild.

            Am Foto steht nichts. Ein „Foto"-Etikett an jeder anderen Reise
            wäre die Kennzeichnung des Normalfalls, und die entwertet die
            Kennzeichnung der Ausnahme.
          -->
          {#if gezeichnete.has(t.id)}
            <span class="gezeichnet">gezeichnet</span>
          {/if}
        {/if}
        <Zeilenmuster
          titel={reisename(t).text}
          unterzeile={fertig ? abgeschlossenAm(t) : unterzeile(t)}
          zusatz={mitwirkende(t)}
          onclick={() => oeffnen(t)}
        >
          <!-- Die Vergleichszeile steht **unter** dem Zeitpunkt und nicht daneben:
               Sie beantwortet eine andere Frage („war die teuer?") als die
               Unterzeile („wann war die?"), und in einer Zeile gelesen wirkte der
               Betrag wie eine Eigenschaft des Abschlussdatums.

               Sie fehlt ersatzlos, solange sie nicht da ist — ein Platzhalter
               („wird geladen …") an jeder gefahrenen Reise machte aus einer
               ruhigen Liste eine Baustelle, und die Zeile ist Zugabe, nicht
               Inhalt. -->
          {#if vergleich}
            <span class="vergleich" data-zahl>{vergleich}</span>
          {/if}
          {#snippet rechts()}
            <Reiseartmarke antrieb={antriebVon(t)} uebernachtung={uebernachtungVon(t)} knapp />
          {/snippet}
          {#snippet nebenhandlungen()}
            <!-- Der Haken sagt „erledigt", der Rückwärtspfeil „wieder her" —
                 beide aus dem Zeichensatz des Hauses und in 44-px-Zielen wie
                 ihre Nachbarn.

                 Abschließen bleibt auch an einer fremden Reise: Es ist eine
                 Ordnungsfrage der **Reise**, nicht des Kontos, und der Server
                 lässt Mitplaner ausdrücklich daran. Wer gemeinsam gefahren ist,
                 darf gemeinsam abhaken. -->
            <Zeichenknopf
              name={fertig ? 'wiederherstellen' : 'haken'}
              titel={fertig
                ? `„${reisename(t).text}“ wieder öffnen`
                : `„${reisename(t).text}“ abschließen`}
              gross
              onclick={() => abschlussUmlegen(t)}
            />
            <!--
              Fest geplant ↔ in Planung.

              Ein Koffer und kein zweites Wort in der Zeile: In welchem
              Zustand die Reise ist, sagt schon die Gruppe, unter der sie
              steht — der Knopf muss nur sagen, was er **tut**, und das steht
              in seinem Titel. Ein Etikett „in Planung" an jeder Zeile wäre
              die Kennzeichnung des Normalfalls, und die entwertet die
              Kennzeichnung der Ausnahme (dieselbe Überlegung wie beim
              Etikett am gezeichneten Titelbild).

              An einer abgeschlossenen und an der laufenden Reise steht er
              nicht: Dort gibt es die Frage nicht mehr, und ein Knopf ohne
              sichtbare Wirkung ist schlimmer als keiner. Die Regel dafür
              steht in `reisegruppen.kannPlanungsstandWaehlen`.

              Auch an einer fremden Reise — `PATCH /trips/{id}` lässt
              Mitplaner ausdrücklich an `settings`, und wie die gemeinsame
              Reise in der Übersicht einsortiert ist, ist gemeinsame Ordnung.
              Dieselbe Begründung wie beim Abschluss-Haken darüber.
            -->
            {#if kannPlanungsstandWaehlen(t, heute)}
              {@const fest = planungsstandVon(t) === 'fest'}
              <Zeichenknopf
                name="koffer"
                titel={fest
                  ? `„${reisename(t).text}“ zurück in Planung nehmen`
                  : `„${reisename(t).text}“ als fest geplant markieren`}
                gross
                onclick={() => planungsstandUmlegen(t)}
              />
            {/if}
            <!--
              Kopieren und Löschen gehören dem Besitzer — beide Wege prüfen im
              Server `trips.user_id` und antworten einem Mitplaner mit 404.
              Einen Knopf stehen zu lassen, der beim Druck „nicht gefunden"
              sagt, wäre eine Lüge über die eigene Reise: Er verspricht eine
              Handlung, die es für diesen Betrachter nicht gibt. Also steht er
              hier nicht.
            -->
            {#if !fremd}
              <!--
                Die Bildwahl. Sie steht an **jeder** eigenen Reise und nicht nur
                an denen mit Karte, obwohl das der naheliegende Weg wäre.

                Der Grund ist die Frage, die diese Zeile nicht beantworten kann:
                Ob eine Reise Fotos hat, weiß der Browser hier nicht. `GET /trips`
                nennt nur das **geteilte** Titelbild — eine Reise, in der alle
                Einträge zurückgehalten sind, sieht von hier aus aus wie eine
                ohne jedes Bild. Der Knopf verschwände also ausgerechnet dort,
                wo man ihn am ehesten sucht: bei einer Reise, in der die
                Automatik nichts gefunden hat.

                Der Preis ist ein vierter Symbolknopf im Nebenhandlungsstreifen,
                und der liegt auf Zeigegeräten bei 0,35 Deckkraft, bis die Zeile
                angesteuert wird. Der Dialog sagt selbst, wenn es nichts zu
                wählen gibt.
              -->
              <Zeichenknopf
                name="bild"
                titel={`Titelbild für „${reisename(t).text}“ wählen`}
                gross
                onclick={() => (bildziel = t)}
              />
              <Zeichenknopf
                name="duplizieren"
                titel={`„${reisename(t).text}“ kopieren`}
                gross
                onclick={() => kopierenOeffnen(t)}
              />
              <Zeichenknopf
                name="schliessen"
                titel={`„${reisename(t).text}“ in den Papierkorb`}
                gefahr
                gross
                onclick={() => loeschen(t)}
              />
            {/if}
          {/snippet}
        </Zeilenmuster>

        <!--
          ── Der Verweis aufs Reisebuch ───────────────────────────────

          Seit dem 7.10.2026 steht das Buch im Planer unter Erinnern ›
          Reisebuch. Hier bleibt ein kleiner Verweis, nur an eigenen,
          abgeschlossenen Reisen (`zeigtReisebuch`) — an einer fremden
          bekäme man im Reiter nur den Satz, dass es das Buch für den
          Besitzer gibt, und an einer laufenden noch keines.
        -->
        {#if zeigtReisebuch(t)}
          <div class="buch">
            <button
              type="button"
              class="buchlink"
              title="Die Reise auf dem Reiter Reisebuch öffnen"
              onclick={() => reisebuchOeffnen(t)}
            >
              Reisebuch
            </button>
          </div>
        {/if}
      </div>
    {/snippet}

    <!--
      Die laufende Reise steht einzeln ganz oben — ohne Überschrift, ohne
      Klappe, ohne Zahl.

      Sie ist keine Gruppe: Es gibt sie höchstens einmal, und eine Überschrift
      über einer einzelnen Zeile behauptet eine Menge, die es nicht gibt. Wer
      die Anwendung unterwegs öffnet, sucht genau diese Reise — sie soll
      dastehen und nicht hinter einem Griff liegen. (Dass `laufend` trotzdem
      eine Liste ist, begründet `reisegruppen`: Zwei sich überlappende Reisen
      sind kein verbotener Zustand, und eine davon zu unterschlagen ließe sie
      spurlos aus der Übersicht verschwinden.)
    -->
    {#if ordnung.laufend.length}
      <div class="liste">
        {#each ordnung.laufend as t (t.id)}
          {@render reisezeile(t)}
        {/each}
      </div>
    {/if}

    <!--
      Darunter die Gruppen — geplant, in Planung, abgeschlossen.

      Leere kommen gar nicht erst an: `reisegruppen` lässt sie weg, denn eine
      Überschrift über nichts ist der Leerzustand, den das Designsystem
      ausdrücklich nicht will.

      Der Griff ist derselbe wie am Papierkorb eine Fläche tiefer, samt
      Begründung: 44 px, weil es ein Ziel ist und kein Text. Zwei verschiedene
      Klappen auf derselben Seite wären zwei Meinungen darüber, wie eine
      Klappe aussieht.
    -->
    {#each ordnung.gruppen as g (g.id)}
      {@const zu = istZugeklappt(g.id, zuGruppen)}
      <section class="gruppe" aria-label={g.titel}>
        <button
          type="button"
          class="klapp"
          aria-expanded={!zu}
          onclick={() => gruppeKlappen(g.id)}
        >
          <span class="pfeil" class:auf={!zu}>
            <Zeichen name="aufklappen" groesse={14} />
          </span>
          {g.titel}
          <span class="zahl" data-zahl>{g.reisen.length}</span>
        </button>

        {#if !zu}
          <div class="liste">
            {#each g.reisen as t (t.id)}
              {@render reisezeile(t)}
            {/each}
          </div>
        {/if}
      </section>
    {/each}
  {/if}

  {#if papierkorb.length}
    <section class="papierkorb">
      <button
        type="button"
        class="klapp"
        aria-expanded={papierkorbOffen}
        onclick={() => (papierkorbOffen = !papierkorbOffen)}
      >
        <span class="pfeil" class:auf={papierkorbOffen}>
          <Zeichen name="aufklappen" groesse={14} />
        </span>
        Papierkorb
        <span class="zahl" data-zahl>{papierkorb.length}</span>
      </button>

      {#if papierkorbOffen}
        <div class="inhalt">
          {#each papierkorb as t (t.id)}
            <Zeilenmuster
              fassung="starr"
              titel={reisename(t).text}
              unterzeile={geloeschtAm(t)}
            >
              {#snippet rechts()}
                <span class="korbknoepfe">
                  <button type="button" class="still" onclick={() => wiederherstellen(t)}>
                    wiederherstellen
                  </button>
                  <button type="button" class="gefahr" onclick={() => endgueltigLoeschen(t)}>
                    endgültig löschen
                  </button>
                </span>
              {/snippet}
            </Zeilenmuster>
          {/each}
          <Hinweis>
            Gelöschte Reisen bleiben erhalten, bis du sie hier endgültig entfernst — ein
            Fehlklick soll keine Wochen Planung kosten.
          </Hinweis>
        </div>
      {/if}
    </section>
  {/if}
{/if}

{#if kopierziel}
  <!-- Ein Vorhang über der ganzen Fläche, wie über der Reibung in der
       Verwaltung: Solange die vier Fragen offen sind, ist nichts anderes
       wichtig — und ein Klick daneben soll nicht in einer Reise landen. -->
  <div class="vorhang">
    <Kopierwahldialog
      reise={reisename(kopierziel).text}
      wahl={kopierwahl}
      laeuft={kopiertGerade}
      fehler={kopierfehler}
      onAendern={(schluessel, angehakt) => (kopierwahl = { ...kopierwahl, [schluessel]: angehakt })}
      onabbrechen={() => (kopierziel = null)}
      onkopieren={kopieren}
    />
  </div>
{/if}

{#if bildziel}
  <!-- Die Reise einmal festhalten: `titelbildSetzen` schließt den Dialog als
       Erstes, und `bildziel` ist danach `null`. Ohne diesen Halt liefe der
       Rückruf auf eine Reise, die es in dem Moment nicht mehr gibt. -->
  {@const ziel = bildziel}
  <!-- Derselbe Vorhang wie über der Kopier-Abfrage, und aus demselben Grund:
       Solange die Fotos zur Wahl stehen, ist nichts anderes wichtig — und ein
       Klick daneben soll nicht in einer Reise landen. -->
  <div class="vorhang">
    <Titelbildwahldialog
      tripId={ziel.id}
      reise={reisename(ziel).text}
      wahl={titelbildAus(ziel.settings)}
      onwaehlen={(wahl) => titelbildSetzen(ziel, wahl)}
      onabbrechen={() => (bildziel = null)}
    />
  </div>
{/if}

<style>
  /* Der Erklärsatz über der Liste — eine Einordnung, kein Hinweiskasten.
     Deshalb ohne Rahmen und in der Nebenfarbe: Er beantwortet eine Frage, die
     nur beim ersten Mal aufkommt, und soll ab dem zweiten Mal nicht im Weg
     stehen. Ausgeblendet wird er in der Expertenfassung zentral über
     `[data-dichte]` (Klasse `nur-einfach`). */
  .erklaersatz {
    margin: 0;
    max-width: 62ch;
    font-size: var(--fs-klein);
    color: var(--text-dim);
  }

  /* Die zwei Griffe der Kopfzeile, in einer Hülle — siehe Markup. */
  .kopfaktionen {
    display: flex;
    align-items: center;
    gap: var(--raum-3);
  }

  /* Das ⋯-Menü. Ein `details` mit absolut gesetzter Liste: Es liegt in der
     Kopfzeile, und eine Liste im Fluss schöbe die Reisen nach unten. */
  .mehr {
    position: relative;
  }
  .mehr summary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* 44 px, wie jedes andere Ziel dieser Fläche — ein Menügriff, den man auf
       dem Telefon zweimal antippen muss, ist kein Griff. */
    min-width: 44px;
    min-height: 44px;
    border-radius: var(--radius-klein);
    cursor: pointer;
    color: var(--text-dim);
    list-style: none;
  }
  .mehr summary::-webkit-details-marker {
    display: none;
  }
  .mehr summary:hover {
    background: var(--surface);
    color: var(--text);
  }
  .mehrliste {
    position: absolute;
    right: 0;
    z-index: 5;
    display: grid;
    gap: var(--raum-3);
    width: max-content;
    max-width: 24rem;
    padding: var(--raum-5);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--bg);
    box-shadow: var(--schatten);
  }
  .mehrliste .fein {
    font-size: var(--fs-fein);
    color: var(--text-dim);
  }

  /* Der Abstand zwischen zwei Reisen — seit der Buchleiste ein Vielfaches des
     Abstands **innerhalb** einer Reise (`--raum-1`) und nicht mehr derselbe
     Wert. Bei 4 zu 4 stand die Leiste genau in der Mitte zwischen zwei Zeilen
     und gehörte dem Auge nach zu keiner von beiden; das ist dieselbe Falle,
     die `Zeilenmuster` bei den Nebenhandlungen schon einmal beschreibt.
     Zugehörigkeit ist eine Frage der Verhältnisse, nicht der absoluten Pixel. */
  .liste {
    display: grid;
    gap: var(--raum-4);
  }

  /* Die Vergleichszeile einer abgeschlossenen Reise.
     So leise wie der `zusatz` des Zeilenmusters, an dem sie sitzt — sie ist
     eine Beigabe und darf den Reisenamen nicht bedrängen. `data-zahl` gibt ihr
     die Ziffernschrift des Hauses, damit „38 €/Tag" und „0,21 €/km" über
     mehrere Zeilen hinweg untereinander stehen; genau dafür ist sie da. */
  .vergleich {
    font-size: var(--fs-fein);
    color: var(--text-schwach);
  }

  /* ── Das Reisebuch an der abgeschlossenen Reise ───────────────────
     Zeile und Buchleiste sind ein Block: eng aneinander (`--raum-1`), während
     zwischen zwei Reisen das Vierfache steht. Und die Leiste rückt um die
     Polsterung des Zeilenmusters ein — sie beginnt dort, wo darüber der
     Reisename beginnt, und liest sich dadurch als dessen Fortsetzung. */
  .reise {
    display: grid;
    gap: var(--raum-1);
  }

  /* ── Die Reise mit Titelbild ──────────────────────────────────────
     Mit Bild wird aus der Zeile eine Karte: Rahmen und Radius um alles, damit
     Bild, Zeile und Buchleiste **ein** Gegenstand sind. Ohne Bild bleibt der
     Block, was er war — kein Rahmen, kein Radius, kein Unterschied zu vorher.

     Der Abstand zwischen Bild und Zeile fällt dabei weg: Ein Spalt zwischen
     Foto und Name sähe aus wie zwei Sachen übereinander. Die Buchleiste
     darunter behält ihren, sie ist ja auch eine eigene Zeile — sie bekommt
     dafür Luft nach unten, sonst klebte sie am Kartenrand. */
  .reise.mitBild {
    gap: 0;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    background: var(--surface);
    /* Der Bezugspunkt für das Etikett am gezeichneten Bild. Es gibt ihn nur
       mit Bild — ohne eines steht auch kein Etikett da. */
    position: relative;
  }
  .reise.mitBild .buch {
    padding-bottom: var(--raum-5);
  }

  /* 160 px wie im Entwurf, auf schmalen Fenstern anteilig weniger: Ein
     Vorschaubild ist 1280 px breit, und ein Drittel Fensterhöhe für ein
     Schmuckbild wäre auf dem Telefon die halbe Liste. `object-fit: cover`,
     weil ein Foto im Querformat und eines im Hochformat dieselbe Zeile tragen
     müssen — verzerrt wird nichts, beschnitten schon. Der Zeiger, weil ein
     Klick aufs Bild die Reise öffnet (seit 30.9.2026, ein reiner Mausweg). */
  .cover {
    display: block;
    cursor: pointer;
    width: 100%;
    height: clamp(110px, 22vh, 160px);
    object-fit: cover;
    /* Der Grund, auf dem das Bild liegt, solange es lädt oder wenn es
       durchscheinende Stellen hat. Kein Springen: Die Höhe steht vorher fest. */
    background: var(--surface-2);
  }
  /* Wortgleich zu den Etiketten der Bildwahl (`Titelbildwahl.svelte`), und
     zwar absichtlich: Es ist dieselbe Sorte Auskunft an derselben Sorte
     Kachel. Weiß auf einem dunklen Feld, weil ein Bild in beiden Themen
     dasselbe Bild ist und ein Etikett in Themenfarben darüber mal steht und
     mal verschwindet. Unten rechts, damit es dem Motiv nicht in die Mitte
     fährt — oben links liegt in der Bildwahl schon die gewählt-Marke. */
  .gezeichnet {
    position: absolute;
    right: var(--raum-3);
    /* Am unteren Rand des Bildes, nicht der Karte: Das Bild hat eine feste
       Höhe, die Karte darunter noch Zeile und Buchleiste. */
    top: calc(clamp(110px, 22vh, 160px) - 22px);
    font-size: var(--fs-mikro);
    line-height: 1.4;
    padding: 1px 6px;
    border-radius: var(--radius-rund);
    color: #fff;
    background: rgb(0 0 0 / 0.62);
  }

  .buch {
    padding-left: var(--raum-5);
  }
  /* Ein Verweis und kein Knopf: leise, klein, ohne Rahmen — die Zeile soll
     nicht wieder eine Werkbank fürs Buch werden. */
  .buchlink {
    border: none;
    background: none;
    padding: 0;
    font-size: var(--fs-fein);
    color: var(--accent);
    cursor: pointer;
  }
  .buchlink:hover {
    text-decoration: underline;
  }

  .neu {
    display: inline-flex;
    align-items: center;
    gap: var(--raum-3);
  }

  /* ── Die Gruppen ──────────────────────────────────────────────────
     Gedämpft, nicht versteckt: Auch die abgeschlossenen Reisen sind noch da
     und werden wieder angesehen. Die Trennlinie oben ist dieselbe wie am
     Papierkorb — die Blöcke der Seite lesen sich damit als Blöcke.

     Die Überschrift ist seit dem 31.8.2026 der Griff selbst (`.klapp`,
     Papierkorb-Fassung) und kein `h3` mehr: Eine Überschrift, die aufklappt,
     und ein Knopf daneben wären zwei Ziele für eine Handlung. Was das für die
     Gliederung kostet, ist bekannt und klein — die Gruppen tragen ihren Namen
     als `aria-label` an der `section`, und ein Vorlesegerät nennt ihn beim
     Betreten. */
  .gruppe {
    margin-top: var(--gruppe-eng);
    border-top: 1px solid var(--border);
    padding-top: var(--raum-5);
    display: grid;
    gap: var(--raum-4);
  }

  .dim {
    color: var(--text-dim);
    margin: 0;
    font-size: var(--fs-fein);
  }

  .fein {
    margin: 0;
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    text-wrap: pretty;
    max-width: 52ch;
  }

  .klein {
    padding: var(--raum-3) var(--raum-5);
    font-size: var(--fs-fein);
  }

  /* ── Anlegen ──────────────────────────────────────────────────── */

  .anlegen {
    display: grid;
    gap: var(--gruppe-weit);
  }
  .anlegen header {
    display: grid;
    gap: var(--raum-3);
  }
  .anlegen h2 {
    margin: 0;
    font-size: var(--fs-titel);
    font-weight: var(--gw-halb);
  }
  .anlegen footer {
    display: grid;
    gap: var(--raum-6);
    border-top: 1px solid var(--border);
    padding-top: var(--gruppe-eng);
  }

  /* ── Profilwahl und Zuhause ──────────────────────────────────────
     Beide sind **leichter** gesetzt als die zwei Fragen darüber: kleinere
     Überschrift, kein großes Tippziel, keine Zweispaltigkeit. Das ist die
     Gestalt der Aussage — dort wird entschieden, hier vorbelegt. */
  .profilwahl {
    display: grid;
    gap: var(--raum-4);
  }
  .profilwahl h3 {
    margin: 0;
    font-size: var(--fs-zeile);
    font-weight: var(--gw-halb);
  }
  .profilwahl .dim {
    margin: 0;
    font-size: var(--fs-fein);
    color: var(--text-dim);
    max-width: 56ch;
    text-wrap: pretty;
  }
  .profile {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--raum-2);
  }
  .profile button {
    width: 100%;
    display: grid;
    gap: 2px;
    text-align: left;
    background: none;
    border: 1px solid var(--border);
    border-radius: var(--radius-klein);
    padding: var(--raum-3) var(--raum-5);
    font: inherit;
    color: var(--text-dim);
    min-height: var(--tippziel);
  }
  .profile button.gewaehlt {
    background: var(--surface);
    border-color: var(--text-schwach);
    color: var(--text);
  }
  .profile .wort {
    font-weight: var(--gw-halb);
  }
  .profile .zeile {
    font-size: var(--fs-mikro);
    color: var(--text-schwach);
    text-wrap: pretty;
  }

  .zuhause {
    display: flex;
    align-items: flex-start;
    gap: var(--raum-4);
    font-size: var(--fs-zeile);
    min-height: var(--tippziel);
    cursor: pointer;
  }
  .zuhause input {
    width: auto;
    flex: none;
    margin: 0.2rem 0 0;
  }
  .zuhause span {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .zuhause .dim {
    font-size: var(--fs-fein);
    color: var(--text-dim);
    text-wrap: pretty;
  }
  .vorschau {
    display: flex;
    align-items: center;
    gap: var(--raum-5);
    flex-wrap: wrap;
    margin: 0;
    font-size: var(--fs-text);
    color: var(--text-dim);
  }
  .los {
    display: flex;
    gap: var(--raum-6);
    align-items: center;
    flex-wrap: wrap;
  }

  /* ── Papierkorb ───────────────────────────────────────────────── */

  .papierkorb {
    border-top: 1px solid var(--border);
    padding-top: var(--raum-5);
  }
  .klapp {
    display: flex;
    align-items: center;
    gap: var(--raum-4);
    border: 0;
    background: none;
    padding: var(--raum-3) 0;
    /* 44 px, weil es ein Ziel ist und kein Text: Vorher war es ein
       `summary` in 0,88 rem, den man erst finden musste. */
    min-height: var(--tippziel);
    color: var(--text-dim);
    font-size: var(--fs-panel);
  }
  .klapp:hover {
    border-color: transparent;
    color: var(--text);
  }
  .pfeil {
    display: inline-flex;
    transition: transform 140ms ease-out;
  }
  .pfeil.auf {
    transform: rotate(90deg);
  }
  .zahl {
    color: var(--text-schwach);
  }

  .inhalt {
    display: grid;
    gap: var(--raum-5);
    padding-top: var(--raum-3);
  }

  .korbknoepfe {
    display: flex;
    gap: var(--raum-6);
    align-items: center;
    flex-wrap: wrap;
  }
  .korbknoepfe button {
    border: 0;
    background: none;
    padding: var(--raum-2) 0;
    font-size: var(--fs-fein);
  }
  .korbknoepfe .still {
    color: var(--accent);
  }
  .korbknoepfe .gefahr {
    color: var(--danger);
  }
  .korbknoepfe button:hover {
    text-decoration: underline;
  }

  /* ── Der Vorhang unter der Kopier-Abfrage ─────────────────────────
     Wortgleich mit dem in der Verwaltung, samt Begründung: Schwarz mit
     Deckung und kein Farbtoken — das Haus hat keines für Vorhänge, und ein
     erfundenes stünde nur an zwei Stellen. Im Hellen dämpft es die Fläche,
     im Dunklen vertieft es sie. */
  .vorhang {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 0.45);
    display: grid;
    place-items: center;
    padding: var(--raum-6);
    z-index: 10;
    /* Ein langer Dialog auf einem kurzen Fenster muss rollen dürfen, sonst
       stehen die Knöpfe unter dem unteren Rand. */
    overflow-y: auto;
  }
</style>
